/**
 * Part 5 — Risk Dashboard: dual 5×5 heatmaps (inherent vs residual) with
 * movement, enrichment-derived risk factor panel, non-mitigated vulnerabilities
 * (with gap drill-down + recovery suggestions), and headline KPIs.
 */
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { toast } from "sonner";
import {
  RefreshCw, TrendingDown, ShieldAlert, Layers, ArrowRight, ChevronDown, ChevronUp,
  Cpu, MapPin, Scale, DollarSign, Building2, Network,
} from "lucide-react";
import RecoverySuggestionsPanel from "./RecoverySuggestionsPanel";

const FACTOR_META = {
  technology: { label: "Technology", Icon: Cpu, cls: "text-violet-600 bg-violet-50" },
  geographic: { label: "Geographic", Icon: MapPin, cls: "text-teal-600 bg-teal-50" },
  regulatory: { label: "Regulatory", Icon: Scale, cls: "text-amber-600 bg-amber-50" },
  financial: { label: "Financial", Icon: DollarSign, cls: "text-emerald-600 bg-emerald-50" },
  industry: { label: "Industry", Icon: Building2, cls: "text-sky-600 bg-sky-50" },
  organizational: { label: "Organizational", Icon: Network, cls: "text-rose-600 bg-rose-50" },
};

function cellColor(score) {
  if (score >= 15) return "bg-red-500 text-white";
  if (score >= 10) return "bg-orange-400 text-white";
  if (score >= 5) return "bg-amber-300 text-slate-800";
  return "bg-emerald-300 text-slate-800";
}

function Heatmap({ grid, title, testId }) {
  // grid[row][col]: row 0 = highest likelihood (top), col 0 = impact 1 (left)
  return (
    <div data-testid={testId}>
      <p className="text-xs font-bold text-slate-600 mb-1.5 text-center">{title}</p>
      <div className="flex">
        <div className="flex flex-col justify-center pr-1">
          <span className="text-[8px] text-slate-400 -rotate-90 whitespace-nowrap origin-center">Likelihood →</span>
        </div>
        <div>
          <div className="grid grid-cols-5 gap-0.5">
            {grid.map((rowArr, r) =>
              rowArr.map((count, c) => {
                const impact = c + 1;
                const likelihood = 5 - r;
                const score = impact * likelihood;
                return (
                  <div key={`${r}-${c}`}
                    className={`w-9 h-9 rounded flex items-center justify-center text-[11px] font-bold ${cellColor(score)} ${count === 0 ? "opacity-30" : ""}`}
                    title={`Impact ${impact} × Likelihood ${likelihood} = ${score} · ${count} risk(s)`}>
                    {count > 0 ? count : ""}
                  </div>
                );
              })
            )}
          </div>
          <p className="text-[8px] text-slate-400 text-center mt-1">Impact →</p>
        </div>
      </div>
    </div>
  );
}

function Kpi({ label, value, sub, accent = "text-slate-800", testId }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-3" data-testid={testId}>
      <p className="text-[10px] uppercase tracking-wide text-slate-400 font-semibold">{label}</p>
      <p className={`text-2xl font-bold ${accent}`}>{value}</p>
      {sub && <p className="text-[10px] text-slate-400">{sub}</p>}
    </div>
  );
}

export default function RiskInsightsDashboard() {
  const { api } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/risk-advanced/dashboard");
      setData(res.data);
    } catch {
      toast.error("Failed to load risk dashboard");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  // Live updates: refetch when the tab/window regains focus and on a light poll,
  // so the heatmaps reflect plan-approval / exercise-completion recalcs without
  // a manual refresh.
  useEffect(() => {
    const onFocus = () => { if (document.visibilityState === "visible") load(); };
    document.addEventListener("visibilitychange", onFocus);
    window.addEventListener("focus", onFocus);
    const poll = setInterval(load, 45000);
    return () => {
      document.removeEventListener("visibilitychange", onFocus);
      window.removeEventListener("focus", onFocus);
      clearInterval(poll);
    };
    /* eslint-disable-next-line */
  }, []);

  if (loading) {
    return <div className="flex items-center gap-2 text-slate-400 text-sm py-12 justify-center" data-testid="risk-dashboard-loading">
      <RefreshCw className="w-4 h-4 animate-spin" /> Calculating inherent &amp; residual risk…</div>;
  }
  if (!data) return null;
  const k = data.kpis;

  return (
    <div className="space-y-5" data-testid="risk-insights-dashboard">
      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2.5">
        <Kpi label="Total Risks" value={k.total_risks} testId="kpi-total-risks" />
        <Kpi label="Enrichment-Derived" value={k.enrichment_derived} accent="text-violet-600" testId="kpi-enrichment-derived" />
        <Kpi label="Avg Inherent" value={k.avg_inherent} testId="kpi-avg-inherent" />
        <Kpi label="Avg Residual" value={k.avg_residual} accent="text-emerald-600" testId="kpi-avg-residual" />
        <Kpi label="Reduction" value={`${k.reduction_pct}%`} accent="text-emerald-600" testId="kpi-reduction" />
        <Kpi label="Unmitigated High" value={k.unmitigated_high} accent={k.unmitigated_high > 0 ? "text-red-600" : "text-slate-800"} testId="kpi-unmitigated" />
        <Kpi label="Recovery Coverage" value={`${k.coverage_pct}%`} accent="text-sky-600" testId="kpi-coverage" />
      </div>

      {/* Dual heatmaps */}
      <div className="bg-white border border-slate-200 rounded-lg p-5" data-testid="dual-heatmaps">
        <div className="flex items-center gap-2 mb-3">
          <Layers className="w-4 h-4 text-sky-700" />
          <h3 className="text-sm font-bold text-slate-800">Inherent vs Residual Risk</h3>
        </div>
        <div className="flex flex-wrap items-center justify-center gap-6">
          <Heatmap grid={data.inherent_heatmap} title="Inherent (before recovery)" testId="inherent-heatmap" />
          <div className="flex flex-col items-center text-emerald-600">
            <TrendingDown className="w-6 h-6" />
            <span className="text-xs font-bold">−{k.reduction_pct}%</span>
            <ArrowRight className="w-5 h-5 mt-1" />
          </div>
          <Heatmap grid={data.residual_heatmap} title="Residual (after recovery)" testId="residual-heatmap" />
        </div>
      </div>

      {/* Enrichment-derived risk factors */}
      <div className="bg-white border border-slate-200 rounded-lg p-5" data-testid="enrichment-factors-panel">
        <h3 className="text-sm font-bold text-slate-800 mb-3">Auto-Discovered Risk Factors</h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2.5">
          {Object.entries(FACTOR_META).map(([key, meta]) => {
            const Icon = meta.Icon;
            const count = data.enrichment_factors.counts[key] || 0;
            return (
              <div key={key} className={`rounded-lg p-3 ${meta.cls}`} data-testid={`factor-${key}`}>
                <Icon className="w-4 h-4 mb-1" />
                <p className="text-xl font-bold">{count}</p>
                <p className="text-[10px] font-medium opacity-80">{meta.label}</p>
              </div>
            );
          })}
        </div>
      </div>

      {/* Non-mitigated vulnerabilities */}
      <div className="bg-white border border-slate-200 rounded-lg p-5" data-testid="non-mitigated-panel">
        <div className="flex items-center gap-2 mb-3">
          <ShieldAlert className="w-4 h-4 text-red-600" />
          <h3 className="text-sm font-bold text-slate-800">Non-Mitigated Vulnerabilities</h3>
          <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-red-100 text-red-700">{data.non_mitigated.length}</span>
        </div>
        {data.non_mitigated.length === 0 ? (
          <p className="text-xs text-emerald-600 py-2">✓ No HIGH residual risks — all critical risks have effective recovery coverage.</p>
        ) : (
          <div className="space-y-2">
            {data.non_mitigated.map((v) => (
              <div key={v.id} className="border border-red-100 rounded-lg" data-testid={`non-mitigated-${v.id}`}>
                <button onClick={() => setExpanded(expanded === v.id ? null : v.id)}
                  className="w-full flex items-center justify-between gap-3 p-3 hover:bg-red-50/40">
                  <div className="flex items-center gap-2 min-w-0 text-left">
                    <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-red-600 text-white">{v.residual_level}</span>
                    <span className="text-sm font-medium text-slate-800 truncate">{v.name}</span>
                    {v.enrichment_source && <span className="text-[10px] text-violet-600 shrink-0">via {v.enrichment_source}</span>}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="text-[10px] text-slate-500">Residual {v.residual_score} / Inherent {v.inherent_score}</span>
                    {expanded === v.id ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                  </div>
                </button>
                {expanded === v.id && (
                  <div className="px-3 pb-3 space-y-3">
                    {v.gaps?.length > 0 && (
                      <div className="flex flex-wrap gap-1.5">
                        {v.gaps.map((g) => (
                          <Link key={g.category} to={g.link}
                            className="text-[10px] font-medium px-2 py-1 rounded-full bg-red-50 text-red-700 border border-red-200 hover:bg-red-100"
                            data-testid={`gap-link-${g.category}`}>
                            {g.label}: {g.status} →
                          </Link>
                        ))}
                      </div>
                    )}
                    <RecoverySuggestionsPanel assessmentId={v.id} onResidualChange={() => load()} />
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Top movements */}
      <div className="bg-white border border-slate-200 rounded-lg p-5" data-testid="risk-movements-panel">
        <h3 className="text-sm font-bold text-slate-800 mb-3">Risk Movement (Inherent → Residual)</h3>
        <div className="space-y-1.5">
          {data.movements.slice(0, 8).map((m) => (
            <div key={m.id} className="flex items-center justify-between gap-3 text-xs" data-testid={`movement-${m.id}`}>
              <span className="font-medium text-slate-700 truncate flex-1">{m.name}</span>
              <span className="text-slate-500">{m.inherent.level} ({m.inherent.score})</span>
              <ArrowRight className="w-3 h-3 text-emerald-500" />
              <span className="font-semibold text-emerald-600">{m.residual.level} ({m.residual.score})</span>
              <span className="text-[10px] text-emerald-600 w-12 text-right">−{m.reduction_pct}%</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
